import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { REST_API_END_POINT } from '../../constants/DefaultValues';
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { IconButton, AppBar, Toolbar } from '@material-ui/core';
import { Menu as MenuIcon } from '@material-ui/icons';
//
import DashboardNavbar from './DashboardNavbar'; // UNCOMMENTED - Show navbar only on dashboard
import DashboardSidebar from './DashboardSidebar';
import FloatingChat from 'src/pages/dashboard/ChatModuleSignwise/FloatingChatButton';
import { Box, Tooltip } from '@material-ui/core';
import { MHidden } from '../../components/@material-extend';
import Lottie from 'lottie-react';
import robotIcon from 'src/assets/Ai Robot Vector Art.json';
import AiChatbot from 'src/components/chatbot';
import useHawkOwlTicketAutoOpen from 'src/hooks/useHawkOwlTicketAutoOpen';
import GlobalOvertimeMonitor from 'src/components/GlobalOvertimeMonitor';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled('div')({
  display: 'flex',
  height: '100vh',
  overflow: 'hidden',
  width: '100%',
  margin: 0,
  padding: 0
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100vh',
  margin: 0,
  paddingTop: APP_BAR_MOBILE + 24, // Mobile header height + padding
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: 30, // No mobile header on desktop
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const MobileHeaderStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('lg')]: {
    display: 'none'
  }
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openChat, setOpenChat] = useState(false);

  useHawkOwlTicketAutoOpen();

  useEffect(() => {
    const handleOpenSefi = () => setOpenChat(true);
    window.addEventListener('open-sefi-chat', handleOpenSefi);
    return () => window.removeEventListener('open-sefi-chat', handleOpenSefi);
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user) {
          axios.post(`${REST_API_END_POINT}settings/statuses/get-all`)
            .then(res => {
              if (res.data.status === 1 && res.data.data) {
                const fetched = res.data.data.map(s => ({
                  label: s.status_name,
                  value: s.id,
                  color: s.color,
                  inactive: s.status === 0
                }));
                localStorage.setItem('portal_statuses', JSON.stringify(fetched));
              }
            }).catch(err => console.error(err));

          axios.post(`${REST_API_END_POINT}settings/priorities/get-all`)
            .then(res => {
              if (res.data.status === 1 && res.data.data) {
                const fetched = res.data.data.map(p => ({
                  value: p.priority,
                  id: p.id,
                  color: p.color,
                  inactive: p.status === 0
                }));
                localStorage.setItem('portal_priorities', JSON.stringify(fetched));
              }
            }).catch(err => console.error(err));

          axios.post(`${REST_API_END_POINT}settings/ticket-types/get-all`)
            .then(res => {
              if (res.data.status === 1 && res.data.data) {
                const fetched = res.data.data.map(t => ({
                  id: t.id,
                  value: t.ticket_type,
                  color: t.color,
                  inactive: t.status === 0
                }));
                localStorage.setItem('portal_ticket_types', JSON.stringify(fetched));
              }
            }).catch(err => console.error(err));
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  return (
    <RootStyle>
      {/* Mobile Header with Hamburger Menu */}
      <MobileHeaderStyle>
        <Toolbar sx={{ minHeight: APP_BAR_MOBILE }}>
          <MHidden width="lgUp">
            <IconButton 
              onClick={() => setOpen(true)} 
              sx={{ mr: 1, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          </MHidden>
        </Toolbar>
      </MobileHeaderStyle>

      <DashboardSidebar 
        isOpenSidebar={open} 
        onCloseSidebar={() => setOpen(false)} 
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />
      <MainStyle>
        <Outlet />
      </MainStyle>
      {/* <FloatingChat /> */}

      {/* Global AI Chatbot Launcher */}
      <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
        <Tooltip title="SEFI" arrow placement="left">
          <IconButton 
            onClick={() => setOpenChat(true)}
            sx={{
              width: 55,
              height: 55,
              background: 'linear-gradient(135deg, #007aff 0%, #0051af 100%)',
              boxShadow: '0 8px 32px rgba(0,122,255,0.3)',
              border: '2px solid rgba(255,255,255,0.2)',
              '&:hover': {
                transform: 'scale(1.1) translateY(-5px)',
                background: 'linear-gradient(135deg, #1a8aff 0%, #0061df 100%)',
                boxShadow: '0 12px 48px rgba(0,122,255,0.4)',
                borderColor: 'rgba(255,255,255,0.4)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Lottie 
              animationData={robotIcon} 
              loop={true} 
              style={{ width: 48, height: 48 }} 
            />
          </IconButton>
        </Tooltip>
      </Box>

      <AiChatbot 
        open={openChat} 
        onClose={() => setOpenChat(false)} 
      />
      <GlobalOvertimeMonitor />
    </RootStyle>
  );
}


// import { useState } from 'react';
// import { Outlet, useLocation } from 'react-router-dom';
// // material
// import { experimentalStyled as styled } from '@material-ui/core/styles';
// //
// import DashboardNavbar from './DashboardNavbar'; // UNCOMMENTED - Show navbar only on dashboard
// import DashboardSidebar from './DashboardSidebar';

// // ----------------------------------------------------------------------

// const APP_BAR_MOBILE = 64;
// const APP_BAR_DESKTOP = 92;

// const RootStyle = styled('div')({
//   display: 'flex',
//   minHeight: '100%',
//   overflow: 'hidden'
// });

// const MainStyle = styled('div')(({ theme, showNavbar }) => ({
//   flexGrow: 1,
//   overflow: 'auto',
//   minHeight: '100%',
//   paddingTop: showNavbar ? APP_BAR_MOBILE + 2 : 30, // Conditional padding based on navbar
//   paddingBottom: theme.spacing(10),
//   [theme.breakpoints.up('lg')]: {
//     paddingTop: showNavbar ? APP_BAR_DESKTOP + 2 : 30, // Conditional padding based on navbar
//     paddingLeft: theme.spacing(2),
//     paddingRight: theme.spacing(2)
//   }
// }));

// // ----------------------------------------------------------------------

// export default function DashboardLayout() {
//   const [open, setOpen] = useState(false);
//   const location = useLocation();
  
//   // Show navbar only on dashboard app page (/dashboard/app)
//   const showNavbar = location.pathname === '/dashboard/app';

//   return (
//     <RootStyle>
//       {showNavbar && <DashboardNavbar onOpenSidebar={() => setOpen(true)} />}
//       <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
//       <MainStyle showNavbar={showNavbar}>
//         <Outlet />
//       </MainStyle>
//     </RootStyle>
//   );
// }
